/**
 * Product Domain Bridge Card Screen (Step 10)
 * Display: Actual vs Ideal chart, bridge strip (current → transition → future), planks.
 * Button: "Activate Product Bridge" → set status, navigate to control.
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Spin, message } from 'antd';
import {
  getProductDomainEntropyMap,
  getProductDomainControlBridge,
  getProductDomainSession,
  getProductDomainQuestions,
  updateProductDomainSession,
} from '../../services/productDomainSessionApi';
import { ActualVsIdealChart } from '../BridgeSession/ActualVsIdealChart';
import type { EntropyMapMetrics } from '../../types/bridgeSession';
import styles from '../BridgeCard/BridgeCard.module.css';

const { Text } = Typography;

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface ProductDomainBridgeCardScreenProps {
  sessionId: string;
  onNext: () => void;
}

export const ProductDomainBridgeCardScreen: React.FC<ProductDomainBridgeCardScreenProps> = ({
  sessionId,
  onNext,
}) => {
  const [session, setSession] = useState<{ product_name?: string | null } | null>(null);
  const [entropyMap, setEntropyMap] = useState<{
    value_ratio: number;
    corrected_value_ratio: number;
    bridge_focus?: string | null;
  } | null>(null);
  const [controlBridge, setControlBridge] = useState<{
    metrics: {
      value_ratio_current: number;
      value_ratio_ideal: number;
      value_ratio_future: number;
      current_revenue: number;
      current_expense: number;
      ideal_revenue?: number;
      ideal_expense?: number;
      recoverable_revenue: number;
      recoverable_expense: number;
    };
    bridge_card_items: Array<{
      id: string;
      question_id: string;
      reason?: string | null;
      priority: number;
      future_condition?: string | null;
      future_answer_score?: number | null;
      assumption_text?: string | null;
      plank_description?: string | null;
      resource_assignment?: string | null;
      estimated_duration?: string | null;
    }>;
  } | null>(null);
  const [questions, setQuestions] = useState<Array<{ question_id: string; text: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [s, map, cb, qs] = await Promise.all([
        getProductDomainSession(sessionId),
        getProductDomainEntropyMap(sessionId),
        getProductDomainControlBridge(sessionId),
        getProductDomainQuestions(sessionId, 'entropy_assessment'),
      ]);
      setSession(s);
      setEntropyMap(map ?? null);
      setControlBridge(cb ?? null);
      setQuestions((qs ?? []).map((q) => ({ question_id: q.question_id, text: q.text })));
      setLoading(false);
    };
    load();
  }, [sessionId]);

  const handleActivate = async () => {
    setActivating(true);
    const updated = await updateProductDomainSession(sessionId, { status: 'bridge_built' });
    setActivating(false);
    if (updated) {
      message.success('Product Bridge activated');
      onNext();
    } else {
      message.error('Failed to activate');
    }
  };

  if (loading) return <Spin tip="Loading..." />;

  const metrics = controlBridge?.metrics ?? {
    value_ratio_current: entropyMap?.value_ratio ?? 0,
    value_ratio_ideal: entropyMap?.corrected_value_ratio ?? 0,
    value_ratio_future: entropyMap?.corrected_value_ratio ?? 0,
    current_revenue: 0,
    current_expense: 0,
    recoverable_revenue: 0,
    recoverable_expense: 0,
  };

  const chartMetrics: EntropyMapMetrics = {
    current_revenue: metrics.current_revenue ?? 0,
    current_expense: metrics.current_expense ?? 0,
    ideal_revenue: metrics.ideal_revenue ?? (metrics.current_revenue ?? 0) + (metrics.recoverable_revenue ?? 0),
    ideal_expense: metrics.ideal_expense ?? Math.max(0, (metrics.current_expense ?? 0) - (metrics.recoverable_expense ?? 0)),
    recoverable_revenue: metrics.recoverable_revenue ?? 0,
    recoverable_expense: metrics.recoverable_expense ?? 0,
  };

  const planks = (controlBridge?.bridge_card_items ?? []).map((item) => {
    const q = questions.find((x) => x.question_id === item.question_id);
    return {
      question_text: q?.text ?? item.reason ?? item.question_id,
      assumption: item.assumption_text ?? undefined,
      action: item.plank_description ?? undefined,
      assignee: item.resource_assignment ?? undefined,
      duration: item.estimated_duration ?? undefined,
      necessary_condition: item.future_condition ?? undefined,
      future_entropy_score: item.future_answer_score,
    };
  });

  const hasChartData = (chartMetrics.current_revenue ?? 0) > 0 || (chartMetrics.current_expense ?? 0) > 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <Typography.Title level={4}>Product Bridge Card</Typography.Title>

      <Card title={session?.product_name || 'Product'} style={{ marginBottom: 24 }}>
        {hasChartData && (
          <div style={{ marginBottom: 24 }}>
            <ActualVsIdealChart metrics={chartMetrics} />
          </div>
        )}

        <section className={styles.bridgeObjectiveSection}>
          <div className={styles.bridgeStrip}>
            <div className={styles.bridgeZoneCurrent}>
              <div className={styles.bridgeZoneLabel}>Current state</div>
              <div className={styles.compactFields}>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Value ratio</span>
                  <span>{metrics.value_ratio_current.toFixed(2)}</span>
                </div>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Revenue</span>
                  <span>{formatCurrency(metrics.current_revenue ?? 0)}</span>
                </div>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Expense</span>
                  <span>{formatCurrency(metrics.current_expense ?? 0)}</span>
                </div>
              </div>
            </div>
            <div className={styles.bridgeConnector} aria-hidden>
              <span className={styles.bridgeConnectorArrow}>→</span>
              <span className={styles.bridgeConnectorText}>Transition</span>
            </div>
            <div className={styles.bridgeZoneBridge}>
              <div className={styles.bridgeZoneLabel}>Transition</div>
              <div className={styles.compactFields}>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Planks</span>
                  <span>{planks.length}</span>
                </div>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Entropy focus</span>
                  <span>{(entropyMap?.bridge_focus ?? '—').toUpperCase()}</span>
                </div>
              </div>
            </div>
            <div className={styles.bridgeConnector} aria-hidden>
              <span className={styles.bridgeConnectorArrow}>→</span>
              <span className={styles.bridgeConnectorText}>Future</span>
            </div>
            <div className={styles.bridgeZoneFuture}>
              <div className={styles.bridgeZoneLabel}>Future state</div>
              <div className={styles.compactFields}>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Target value ratio</span>
                  <span>{metrics.value_ratio_ideal.toFixed(2)}</span>
                </div>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Ideal revenue</span>
                  <span>{formatCurrency(chartMetrics.ideal_revenue ?? 0)}</span>
                </div>
                <div className={styles.compactRow}>
                  <span className={styles.compactLabel}>Ideal expense</span>
                  <span>{formatCurrency(chartMetrics.ideal_expense ?? 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {planks.length > 0 && (
          <section className={styles.frictionPlanksSection} style={{ marginTop: 24 }}>
            <div className={styles.sectionMainTitle}>Action planks</div>
            <div className={styles.frictionPlanksList}>
              {planks.map((plank, index) => (
                <div key={index} className={styles.frictionPlankItem}>
                  <div className={styles.plankQuestion}>{plank.question_text}</div>
                  <div className={styles.plankStrip}>
                    <div className={styles.plankZoneCurrent}>
                      <div className={styles.plankZoneLabel}>Assumption</div>
                      <div className={styles.plankCellContent}>{plank.assumption || '—'}</div>
                    </div>
                    <div className={styles.plankConnector} aria-hidden>→</div>
                    <div className={styles.plankZoneBridge}>
                      <div className={styles.plankZoneLabel}>Action</div>
                      <div className={styles.plankCellContent}>{plank.action || '—'}</div>
                      <div className={styles.compactRow}>
                        <span className={styles.compactLabel}>Assignee</span>
                        <span>{plank.assignee || '—'}</span>
                      </div>
                      <div className={styles.compactRow}>
                        <span className={styles.compactLabel}>Duration</span>
                        <span>{plank.duration || '—'}</span>
                      </div>
                    </div>
                    <div className={styles.plankConnector} aria-hidden>→</div>
                    <div className={styles.plankZoneFuture}>
                      <div className={styles.plankZoneLabel}>Future condition</div>
                      <div className={styles.plankCellContent}>{plank.necessary_condition || '—'}</div>
                      {plank.future_entropy_score != null && (
                        <div className={styles.compactRow}>
                          <span className={styles.compactLabel}>Score</span>
                          <span>{plank.future_entropy_score}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </Card>

      <Button
        type="primary"
        size="large"
        onClick={handleActivate}
        loading={activating}
      >
        Activate Product Bridge
      </Button>
    </div>
  );
};
